/**
 * Literature Collection - Index
 * Aggregates all poems and stories with utility functions
 */

// Import all poems
import cremationOfSamMcGee from './poems/the-cremation-of-sam-mcgee.js';
import atlanticTelegraph from './poems/the-atlantic-telegraph.js';
import lineGang from './poems/the-line-gang.js';
import deepSeaCables from './poems/the-deep-sea-cables.js';
import howCyrusLaidCable from './poems/how-cyrus-laid-the-cable.js';
import radioPoem from './poems/radio-poem.js';
import comingRoundTheBend from './poems/coming-round-the-bend.js';
import songOfPlug from './poems/song-of-the-plug.js';
import caseyJones from './poems/casey-jones.js';
import amateursCode from './stories/amateurs-code.js';

// Import all stories
import jawsOfDeath from './stories/into-the-jaws-of-death.js';
import volcanograph from './stories/the-volcanograph.js';
import signalTower from './stories/the-signal-tower.js';
import nightWire from './stories/the-night-wire.js';
import stockbrokerClerk from './stories/the-stockbroker-clerk.js';
import telegraphOperator from './stories/the-telegraph-operators-story.js';
import radioBeasts from './novels/the-radio-beasts.js';
import radioMan from './novels/the-radio-man.js';
import radioPlanet from './novels/the-radio-planet.js';
import { wiredLove } from './novels/wired-love.js';
import wireDevils from './novels/the-wire-devils.js';
import radioVRays from './stories/radio-v-rays.js';
import signalMan from './stories/the-signal-man.js';
import wireless from './stories/wireless.js';


/**
 * All literature content aggregated
 */
export const LITERATURE = [
    cremationOfSamMcGee,
    atlanticTelegraph,
    lineGang,
    deepSeaCables,
    jawsOfDeath,
    volcanograph,
    signalTower,
    nightWire,
    stockbrokerClerk,
    howCyrusLaidCable,
    radioPoem,
    telegraphOperator,
    radioBeasts,
    radioMan,
    radioPlanet,
    wiredLove,
    wireDevils,
    radioVRays,
    signalMan,
    comingRoundTheBend,
    wireless,
    songOfPlug,
    amateursCode,
    caseyJones,
];

/**
 * All poems
 */
export const POEMS = [
    cremationOfSamMcGee,
    atlanticTelegraph,
    lineGang,
    deepSeaCables,
    howCyrusLaidCable,
    radioPoem,
    comingRoundTheBend,
    songOfPlug,
    caseyJones,
];

/**
 * All short stories
 */
export const STORIES = [
    jawsOfDeath,
    volcanograph,
    signalTower,
    nightWire,
    stockbrokerClerk,
    telegraphOperator,
    radioVRays,
    signalMan,
    wireless,
    amateursCode,
];

/**
 * All novels
 */
export const NOVELS = [
    radioBeasts,
    radioMan,
    radioPlanet,
    wiredLove,
    wireDevils,
];

/**
 * Utility function to get literature by ID
 */
export function getLiteratureById(id) {
    // Check main list first
    const item = LITERATURE.find(item => item.id === id);
    if (item) return item;

    // Check inside novels (chapters)
    for (const novel of NOVELS) {
        if (novel.chapters) {
            const chapter = novel.chapters.find(ch => ch.id === id);
            if (chapter) return chapter;
        }
    }

    return undefined;
}

/**
 * Utility function to get literature by type
 */
export function getLiteratureByType(type) {
    return LITERATURE.filter(item => item.type === type);
}

/**
 * Utility function to get all poems
 */
export function getAllPoems() {
    return POEMS;
}

/**
 * Utility function to get all short stories
 */
export function getAllStories() {
    return STORIES;
}

/**
 * Utility function to get all novels
 */
export function getAllNovels() {
    return NOVELS;
}

/**
 * Utility function to get literature by difficulty
 */
export function getLiteratureByDifficulty(difficulty) {
    return LITERATURE.filter(item => item.metadata.difficulty === difficulty);
}

/**
 * Utility function to get literature by author
 */
export function getLiteratureByAuthor(author) {
    return LITERATURE.filter(item =>
        item.author.toLowerCase().includes(author.toLowerCase())
    );
}

/**
 * Utility function to get literature by genre
 */
export function getLiteratureByGenre(genre) {
    return LITERATURE.filter(item =>
        item.genres.some(g => g.toLowerCase().includes(genre.toLowerCase()))
    );
}

/**
 * Utility function to sanitize text for Morse code transmission
 * Converts to uppercase, removes special characters, uses BK for breaks
 */
import { sanitizeForMorse as cleanMorse } from '../../utils/textSanitizer.js';

export function sanitizeForMorse(text) {
    return cleanMorse(text)
        .replace(/\n\n+/g, ' BK ')  // Keep existing behavior for paragraph breaks
        .replace(/\n/g, ' ');       // AND existing behavior for line breaks
}

/**
 * Calculate accurate character length
 */
export function calculateCharacterLength(text) {
    return text.length;
}

/**
 * Get a random literature piece
 */
export function getRandomLiterature() {
    return LITERATURE[Math.floor(Math.random() * LITERATURE.length)];
}

/**
 * Get a random poem
 */
export function getRandomPoem() {
    return POEMS[Math.floor(Math.random() * POEMS.length)];
}

/**
 * Get a random story
 */
export function getRandomStory() {
    if (STORIES.length === 0) return null;
    return STORIES[Math.floor(Math.random() * STORIES.length)];
}

/**
 * Get all comprehension questions for a piece of literature
 */
export function getComprehensionQuestions(id) {
    const item = getLiteratureById(id);
    return item ? item.comprehensionQuestions : [];
}

/**
 * Get comprehension questions by difficulty
 */
export function getComprehensionQuestionsByDifficulty(id, difficulty) {
    const item = getLiteratureById(id);
    if (!item) return [];
    return item.comprehensionQuestions.filter(q => q.difficulty === difficulty);
}

/**
 * Get all segments for a piece of literature
 */
export function getSegments(id) {
    const item = getLiteratureById(id);
    return item && item.segments ? item.segments : [];
}

/**
 * Get a specific segment by ID
 */
export function getSegmentById(literatureId, segmentId) {
    const segments = getSegments(literatureId);
    return segments.find(seg => seg.id === segmentId);
}

/**
 * Get segment by index (0-based)
 */
export function getSegmentByIndex(literatureId, index) {
    const segments = getSegments(literatureId);
    return segments[index];
}

/**
 * Get comprehension questions for a specific segment
 */
export function getSegmentQuestions(literatureId, segmentId, allQuestions) {
    const segment = getSegmentById(literatureId, segmentId);
    if (!segment) return [];

    // Support for direct questions array (newer format)
    if (segment.questions && segment.questions.length > 0) {
        return segment.questions.map(q => ({
            ...q,
            question: q.question || q.text, // Normalize question text
            answer: q.answer || q.correctAnswer // Normalize answer property
        }));
    }

    const item = getLiteratureById(literatureId);
    if (!item) return [];

    // Legacy: Get the question IDs from the segment
    const questionIds = segment.comprehensionQuestions || [];

    // Return the actual question objects
    if (!item.comprehensionQuestions) return [];
    return item.comprehensionQuestions.filter(q => questionIds.includes(q.id));
}

/**
 * Get estimated transmission time for a segment at given WPM
 */
export function getSegmentEstimatedTime(literatureId, segmentId, wpm = 20) {
    const segment = getSegmentById(literatureId, segmentId);
    if (!segment || !segment.estimatedSeconds) return null;

    // Find closest WPM estimate
    const estimates = segment.estimatedSeconds;
    if (estimates[`wpm${wpm}`]) {
        return estimates[`wpm${wpm}`];
    }

    // Interpolate if exact WPM not available
    const availableWpms = Object.keys(estimates)
        .map(key => parseInt(key.replace('wpm', '')))
        .sort((a, b) => a - b);

    // Find the two closest WPMs to interpolate between
    let lowerWpm = availableWpms[0];
    let upperWpm = availableWpms[availableWpms.length - 1];

    for (let i = 0; i < availableWpms.length - 1; i++) {
        if (availableWpms[i] <= wpm && availableWpms[i + 1] >= wpm) {
            lowerWpm = availableWpms[i];
            upperWpm = availableWpms[i + 1];
            break;
        }
    }

    // Linear interpolation
    const lowerTime = estimates[`wpm${lowerWpm}`];
    const upperTime = estimates[`wpm${upperWpm}`];
    const ratio = (wpm - lowerWpm) / (upperWpm - lowerWpm);

    return Math.round(lowerTime + (upperTime - lowerTime) * ratio);
}

/**
 * Get total number of segments for a piece of literature
 */
export function getSegmentCount(literatureId) {
    const segments = getSegments(literatureId);
    return segments.length;
}

/**
 * Check if there's a next segment
 */
export function hasNextSegment(literatureId, currentSegmentIndex) {
    const count = getSegmentCount(literatureId);
    return currentSegmentIndex < count - 1;
}

/**
 * Check if there's a previous segment
 */
export function hasPreviousSegment(currentSegmentIndex) {
    return currentSegmentIndex > 0;
}
